import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  Package, Clock, CheckCircle, Truck, Plus, ChevronRight, AlertCircle, 
  ArrowLeft, CreditCard, MapPin, Calendar, Hash, FileText, Circle, XCircle, Trash2, Edit
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  'draft': { 
    label: 'Draft', 
    color: 'bg-gray-100 text-gray-600 border-gray-300 border-dashed',
    dotColor: 'bg-gray-400',
    description: 'Submission not completed - continue or delete',
    isDraft: true
  },
  'awaiting_shipment': { 
    label: 'Awaiting Shipment', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    dotColor: 'bg-blue-500',
    description: 'Please ship your cards to our facility'
  },
  'received': { 
    label: 'Cards Received', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    dotColor: 'bg-yellow-500',
    description: 'Your cards have arrived and are in queue'
  },
  'payment_processed': { 
    label: 'Payment Processed', 
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    dotColor: 'bg-orange-500',
    description: 'Payment has been processed successfully'
  },
  'grading_complete': { 
    label: 'Grading Complete', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    dotColor: 'bg-purple-500',
    description: 'Your cards have been graded'
  },
  'id_and_encapsulation': { 
    label: 'Encapsulating', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    dotColor: 'bg-purple-500',
    description: 'Cards are being encapsulated in holders'
  },
  'shipped': { 
    label: 'Shipped', 
    color: 'bg-green-100 text-green-800 border-green-200',
    dotColor: 'bg-green-500',
    description: 'Your cards are on their way!'
  },
  'complete': { 
    label: 'Delivered', 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    dotColor: 'bg-gray-500',
    description: 'Order complete'
  }
};

const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || { 
    label: status, 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    dotColor: 'bg-gray-500',
    description: ''
  };
};

// Submission Card Component
const SubmissionCard = ({ submission, onClick, onDelete, onContinue }) => {
  const statusConfig = getStatusConfig(submission.status);
  const isDraft = statusConfig.isDraft;
  
  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete this draft submission (${submission.submission_number})? This cannot be undone.`)) {
      onDelete(submission.id);
    }
  };

  const handleContinue = (e) => {
    e.stopPropagation();
    onContinue(submission.id);
  };
  
  return (
    <div 
      onClick={isDraft ? undefined : onClick}
      data-testid={`submission-card-${submission.id}`}
      className={`bg-white rounded-xl border p-6 transition-all ${
        isDraft 
          ? 'border-dashed border-gray-300 bg-gray-50' 
          : 'border-gray-200 hover:shadow-md hover:border-[#003594]/30 cursor-pointer'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className={`text-lg font-bold ${isDraft ? 'text-gray-500' : 'text-[#003594]'}`}>
              {submission.submission_number}
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Package className="w-4 h-4" />
              {submission.card_count} card{submission.card_count !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(submission.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </span>
            <span className="font-medium text-gray-900">
              ${submission.total_price?.toFixed(2)}
              {submission.voucher_discount > 0 && (
                <span className="text-xs text-green-600 ml-1">(incl. discount)</span>
              )}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2">{statusConfig.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDelete}
                className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-draft-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
              <Button 
                size="sm" 
                onClick={handleContinue}
                className="flex items-center gap-2 bg-[#003594] hover:bg-[#002554]"
                data-testid="continue-draft-btn"
              >
                <Edit className="w-4 h-4" />
                Continue
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" className="flex items-center gap-2" data-testid="view-details-btn">
              View Details
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Submission Detail Component
const SubmissionDetail = ({ submissionId, onBack }) => {
  const [submission, setSubmission] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubmissionDetail();
  }, [submissionId]);

  const fetchSubmissionDetail = async () => {
    try {
      setIsLoading(true);
      // Fetch submission with cards
      const response = await axios.get(`${API}/submissions/${submissionId}`);
      setSubmission(response.data);
      
      // Fetch status history
      try {
        const historyResponse = await axios.get(`${API}/submissions/${submissionId}/history`);
        setStatusHistory(historyResponse.data || []);
      } catch (e) {
        // Status history endpoint might not exist yet, that's ok
        console.log('No status history available');
      }
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      setError('Failed to load submission details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-500">Loading submission details...</p>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error || 'Submission not found'}</p>
        <Button onClick={onBack} variant="outline">Back to Dashboard</Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(submission.status);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button 
        onClick={onBack}
        data-testid="back-to-dashboard-btn"
        className="flex items-center gap-2 text-[#003594] hover:underline font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{submission.submission_number}</h2>
            <span className={`inline-flex px-4 py-2 rounded-full text-sm font-medium border ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-3xl font-bold text-[#003594]">${(submission.total_price || 0).toFixed(2)}</p>
            {submission.shipping_cost !== undefined && submission.shipping_cost !== null ? (
              <p className="text-xs text-gray-400">
                (Grading: ${((submission.grading_cost) || (submission.total_price - (submission.shipping_cost || 0) - (submission.insurance_cost || 0))).toFixed(2)} + Shipping: ${(submission.shipping_cost).toFixed(2)}
                {submission.insurance_cost > 0 ? ` + Insurance: $${submission.insurance_cost.toFixed(2)}` : ''})
              </p>
            ) : (
              <p className="text-xs text-gray-400">(grading total)</p>
            )}
            {submission.voucher_discount > 0 && (
              <p className="text-xs text-green-600 mt-1">
                Discount applied: -${submission.voucher_discount.toFixed(2)}
              </p>
            )}
          </div>
        </div>

        {/* Customer-friendly Payment Status - No technical details shown */}
        {submission.status === 'received' && (
          <div className={`rounded-lg p-4 mb-4 ${
            submission.payment_failed 
              ? 'bg-amber-50 border border-amber-200' 
              : 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-start gap-3">
              {submission.payment_failed ? (
                <>
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Payment Processing</p>
                    <p className="text-sm text-amber-700 mt-1">
                      We&apos;re working to process your payment. If there are any issues with your payment method, 
                      we&apos;ll reach out to you directly. You can also update your payment information in your account settings.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Payment Pending</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Your cards have been received. Payment will be processed shortly.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Submission Info Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Cards</p>
            <p className="font-semibold text-gray-900">{submission.card_count}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Price per Card</p>
            <p className="font-semibold text-gray-900">${submission.price_per_card?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Pricing Tier</p>
            <p className="font-semibold text-gray-900">{submission.pricing_tier}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="font-semibold text-gray-900">
              {new Date(submission.created_at).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Status Timeline - Structured Workflow */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#003594]" />
            Order Progress
          </h3>
          
          {/* Packing Slip - Right aligned, only for awaiting_shipment */}
          {(submission.status === 'awaiting_shipment' || submission.status === 'draft') && (
            <div className="bg-[#003594] rounded-lg px-4 py-3 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <FileText className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Packing Slip</p>
                    <p className="text-xs text-blue-100">Print and include with your cards for faster processing</p>
                  </div>
                </div>
                <Link 
                  to={`/submissions/${submission.id}/print`} 
                  target="_blank"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-[#003594] hover:bg-gray-100 transition-colors"
                  data-testid="packing-slip-link"
                >
                  <FileText className="w-4 h-4" />
                  View / Print
                </Link>
              </div>
            </div>
          )}
        </div>
        
        {/* Customer Timeline Steps */}
        {(() => {
          // Define the customer-facing workflow steps
          const workflowSteps = [
            { 
              id: 'submitted', 
              name: 'Submission Created', 
              description: 'Your order has been submitted',
              statuses: ['awaiting_shipment', 'received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
              timestamp: submission.created_at
            },
            { 
              id: 'ship', 
              name: 'Ship Your Cards', 
              description: 'ship_step', // Special flag for custom rendering
              statuses: ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
              timestamp: null // No specific timestamp for this step
            },
            { 
              id: 'received', 
              name: 'Cards Received', 
              description: 'We have received your cards',
              statuses: ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
              timestamp: submission.received_at
            },
            { 
              id: 'payment', 
              name: 'Payment Processed', 
              description: 'Payment has been collected',
              statuses: ['payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
              timestamp: submission.payment_collected_at
            },
            { 
              id: 'grading', 
              name: 'Grading in Progress', 
              description: 'Your cards are being professionally graded',
              statuses: ['grading_complete', 'id_and_encapsulation', 'shipped', 'complete'],
              timestamp: submission.grading_completed_at
            },
            { 
              id: 'shipped', 
              name: 'Shipped to You', 
              description: 'Your graded cards are on their way',
              statuses: ['shipped', 'complete'],
              timestamp: submission.shipped_at
            },
            { 
              id: 'complete', 
              name: 'Delivered', 
              description: 'Order complete',
              statuses: ['complete'],
              timestamp: submission.completed_at
            }
          ];

          // Determine current step index based on status
          const currentStatus = submission.status;
          const statusToStepIndex = {
            'awaiting_shipment': 1, // "Ship Your Cards" is current
            'received': 2, // "Cards Received" is current (or payment processing)
            'payment_processed': 4, // "Grading in Progress" is current
            'grading_complete': 4,
            'id_and_encapsulation': 4,
            'shipped': 5, // "Shipped to You" is current
            'complete': 6 // All complete
          };
          
          const currentStepIndex = statusToStepIndex[currentStatus] ?? 0;
          const isPaymentFailed = submission.payment_failed && currentStatus === 'received';

          return (
            <div className="space-y-0">
              {workflowSteps.map((step, index) => {
                const isCompleted = step.statuses.includes(currentStatus) && index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const isPending = index > currentStepIndex;
                const isPaymentStep = step.id === 'payment';
                
                // Special handling for "Ship Your Cards" - it's complete once received
                const isShipStep = step.id === 'ship';
                const shipStepCompleted = isShipStep && ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus);
                
                // Special handling for Cards Received step when payment failed
                const showPaymentProcessing = step.id === 'received' && isPaymentFailed;
                
                const stepCompleted = isCompleted || (index === 0) || shipStepCompleted;
                const stepCurrent = isCurrent && !stepCompleted;
                
                return (
                  <div key={step.id} className="flex items-start gap-4">
                    {/* Step indicator */}
                    <div className="relative flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium z-10 ${
                        stepCompleted 
                          ? 'bg-green-500 text-white' 
                          : stepCurrent
                            ? 'bg-[#003594] text-white ring-4 ring-[#003594]/20'
                            : 'bg-gray-200 text-gray-500'
                      }`}>
                        {stepCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      {/* Connector line */}
                      {index < workflowSteps.length - 1 && (
                        <div className={`w-0.5 h-12 ${
                          stepCompleted ? 'bg-green-500' : 'bg-gray-200'
                        }`}></div>
                      )}
                    </div>
                    
                    {/* Step content */}
                    <div className={`flex-1 pb-8 ${index === workflowSteps.length - 1 ? 'pb-0' : ''}`}>
                      <p className={`font-medium ${
                        stepCompleted 
                          ? 'text-green-700' 
                          : stepCurrent 
                            ? 'text-[#003594]' 
                            : 'text-gray-400'
                      }`}>
                        {step.name}
                      </p>
                      <p className={`text-sm ${stepCompleted || stepCurrent ? 'text-gray-600' : 'text-gray-400'}`}>
                        {step.description === 'ship_step' ? (
                          <>
                            See our <Link to="/shipping-instructions" className="text-[#003594] hover:underline">shipping guide</Link> for packing tips.
                          </>
                        ) : (
                          step.description
                        )}
                      </p>
                      
                      {/* Timestamp or status */}
                      {stepCompleted && step.timestamp && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(step.timestamp).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: 'numeric', minute: '2-digit', hour12: true
                          })}
                        </p>
                      )}
                      {stepCurrent && !showPaymentProcessing && (
                        <p className="text-xs text-[#003594] font-medium mt-1">In Progress</p>
                      )}
                      {showPaymentProcessing && step.id === 'received' && (
                        <p className="text-xs text-amber-600 font-medium mt-1">Payment Processing...</p>
                      )}
                      {isPending && !stepCompleted && !stepCurrent && (
                        <p className="text-xs text-gray-400 mt-1">Pending</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Shipping Address */}
      {submission.shipping_address && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#003594]" />
            Return Shipping Address
          </h3>
          <div className="text-gray-600">
            <p className="font-medium text-gray-900">{submission.shipping_address.name}</p>
            <p>{submission.shipping_address.street_address}</p>
            <p>{submission.shipping_address.city}, {submission.shipping_address.state} {submission.shipping_address.zip_code}</p>
            {submission.shipping_address.phone && <p>{submission.shipping_address.phone}</p>}
          </div>
        </div>
      )}

      {/* Tracking Number */}
      {submission.tracking_number && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#003594]" />
            Tracking Information
          </h3>
          <p className="text-gray-600">
            Tracking Number: <span className="font-mono font-semibold text-gray-900">{submission.tracking_number}</span>
          </p>
        </div>
      )}

      {/* Card Details */}
      {submission.cards && submission.cards.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#003594]" />
            Card Details ({submission.cards.length} entered)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">#</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Year</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Set/Brand</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Card #</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Player</th>
                </tr>
              </thead>
              <tbody>
                {submission.cards.map((card, index) => (
                  <tr key={card.id} className="border-b border-gray-100">
                    <td className="py-2 px-3 text-gray-600">{index + 1}</td>
                    <td className="py-2 px-3 text-gray-900">{card.year || '-'}</td>
                    <td className="py-2 px-3 text-gray-900">{card.set_brand || '-'}</td>
                    <td className="py-2 px-3 text-gray-900">{card.card_number || '-'}</td>
                    <td className="py-2 px-3 text-gray-900">{card.player_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Information */}
      {submission.stripe_payment_method_id && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#003594]" />
            Payment Information
          </h3>
          <div className="flex items-center gap-2 text-gray-600">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Payment method on file</span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Your card will be charged when we receive your cards for grading.
          </p>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Page
const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [showAllPast, setShowAllPast] = useState(false);
  const [redirectError, setRedirectError] = useState(location.state?.error || null);

  useEffect(() => {
    fetchSubmissions();
    // Clear the error from location state after displaying
    if (location.state?.error) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.error]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions`);
      setSubmissions(response.data);
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
      setError('Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDraft = async (submissionId) => {
    try {
      await axios.delete(`${API}/submissions/${submissionId}`);
      // Refresh the list
      fetchSubmissions();
    } catch (err) {
      console.error('Failed to delete draft:', err);
      alert(err.response?.data?.detail || 'Failed to delete draft submission');
    }
  };

  const handleContinueDraft = (submissionId) => {
    // Navigate to the submission flow - it will resume from where they left off
    navigate(`/submit?resume=${submissionId}`);
  };

  // Separate active, draft, and past submissions
  const draftSubmissions = submissions
    .filter(s => s.status === 'draft')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const activeSubmissions = submissions
    .filter(s => !['complete', 'draft'].includes(s.status))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  const pastSubmissions = submissions
    .filter(s => s.status === 'complete')
    .sort((a, b) => new Date(b.shipped_at || b.created_at) - new Date(a.shipped_at || a.created_at));

  const displayedPastSubmissions = showAllPast ? pastSubmissions : pastSubmissions.slice(0, 10);

  // If viewing a submission detail
  if (selectedSubmissionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <SubmissionDetail 
              submissionId={selectedSubmissionId} 
              onBack={() => setSelectedSubmissionId(null)} 
            />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Header */}
        <div className="bg-[#002554] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Welcome back, {user?.name?.split(' ')[0] || 'there'}!
                </h1>
                <p className="text-gray-200">
                  {activeSubmissions.length > 0 
                    ? `You have ${activeSubmissions.length} active submission${activeSubmissions.length !== 1 ? 's' : ''}`
                    : 'Manage your card submissions and track their progress.'}
                </p>
              </div>
              <Link to="/new-submission" className="mt-4 md:mt-0">
                <Button className="bg-white text-[#003594] hover:bg-gray-100 px-6 py-3 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  New Submission
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Redirect Error Banner */}
          {redirectError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700 flex-grow">{redirectError}</p>
              <button 
                onClick={() => setRedirectError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {isLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading submissions...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : submissions.length === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-testid="empty-state">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No submissions yet</h3>
              <p className="text-gray-500 mb-6">Ready to submit cards for grading?</p>
              <Link to="/new-submission">
                <Button className="bg-[#003594] hover:bg-[#002554] text-white px-8 py-3 flex items-center gap-2 mx-auto">
                  <Plus className="w-5 h-5" />
                  Start New Submission
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              {/* All Submissions Section (drafts + active combined) */}
              <section data-testid="submissions-section">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Submissions</h2>
                {draftSubmissions.length === 0 && activeSubmissions.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No active submissions</h3>
                    <p className="text-gray-500 mb-6">Ready to submit more cards?</p>
                    <Link to="/new-submission">
                      <Button className="bg-[#003594] hover:bg-[#002554] text-white px-6 flex items-center gap-2 mx-auto">
                        <Plus className="w-5 h-5" />
                        Start New Submission
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {[...draftSubmissions, ...activeSubmissions].map((submission) => (
                      <SubmissionCard 
                        key={submission.id} 
                        submission={submission}
                        onClick={() => submission.status !== 'draft' ? setSelectedSubmissionId(submission.id) : null}
                        onDelete={handleDeleteDraft}
                        onContinue={handleContinueDraft}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Past Submissions Section */}
              {pastSubmissions.length > 0 && (
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Past Submissions</h2>
                  <div className="space-y-4">
                    {displayedPastSubmissions.map((submission) => (
                      <SubmissionCard 
                        key={submission.id} 
                        submission={submission}
                        onClick={() => setSelectedSubmissionId(submission.id)}
                        onDelete={handleDeleteDraft}
                        onContinue={handleContinueDraft}
                      />
                    ))}
                  </div>
                  {pastSubmissions.length > 10 && !showAllPast && (
                    <div className="text-center mt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowAllPast(true)}
                        className="text-[#003594]"
                      >
                        View All ({pastSubmissions.length} total)
                      </Button>
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
